import React from 'react';
import {
    PurchaseOrderLineStatus,
    PurchaseOrderStatus,
} from '../../../../types/procurement.types';
import { cn } from '../../../../components/ui/utils';

/**
 * Shared presentation vocabulary for Procurement → Purchase Orders.
 *
 * Every screen (list, Kanban, detail, receiving, approvals) labels and colours a status the
 * same way; keeping the tables here is what stops the Kanban lane header and the detail pill
 * from disagreeing. Mirrors the prototype's status colours.
 */

/** Lifecycle order — the Kanban lanes, the filter chips and the detail rail all read it. */
export const PURCHASE_ORDER_STATUS_ORDER: PurchaseOrderStatus[] = [
    'draft',
    'submitted',
    'pending_approval',
    'approved',
    'po_issued',
    'receiving',
    'ready_to_enter',
    'entered',
];

/** Off-flow outcomes shown after the lifecycle lanes. */
export const PURCHASE_ORDER_OFF_FLOW_STATUSES: PurchaseOrderStatus[] = ['rejected', 'cancelled'];

export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
    draft: 'Draft',
    submitted: 'Submitted',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    po_issued: 'PO Issued',
    receiving: 'Receiving',
    ready_to_enter: 'Ready to Enter',
    entered: 'Entered',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
};

const STATUS_STYLES: Record<PurchaseOrderStatus, string> = {
    draft: 'text-slate-700 bg-slate-100',
    submitted: 'text-slate-700 bg-slate-100',
    pending_approval: 'text-amber-700 bg-amber-50 ring-1 ring-amber-200',
    approved: 'text-sky-700 bg-sky-50',
    po_issued: 'text-slate-900 bg-slate-100',
    receiving: 'text-amber-700 bg-amber-50',
    ready_to_enter: 'text-blue-700 bg-blue-50',
    entered: 'text-emerald-800 bg-emerald-100 ring-1 ring-emerald-200',
    rejected: 'text-red-700 bg-red-50 ring-1 ring-red-200',
    cancelled: 'text-red-700 bg-red-50 ring-1 ring-red-200',
};

export const purchaseOrderStatusLabel = (status: PurchaseOrderStatus | string) =>
    PURCHASE_ORDER_STATUS_LABELS[status as PurchaseOrderStatus]
    ?? String(status).replace(/_/g, ' ');

export const PurchaseOrderStatusPill: React.FC<{
    status: PurchaseOrderStatus | string;
    className?: string;
}> = ({ status, className }) => (
    <span className={cn(
        'inline-flex items-center text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap',
        STATUS_STYLES[status as PurchaseOrderStatus] ?? 'text-slate-700 bg-slate-100',
        className
    )}>
        {purchaseOrderStatusLabel(status)}
    </span>
);

const LINE_STATUS_STYLES: Record<PurchaseOrderLineStatus, { label: string; className: string }> = {
    open: { label: 'Open', className: 'text-slate-700 bg-slate-100' },
    partially_received: { label: 'Partially Received', className: 'text-amber-700 bg-amber-50' },
    received: { label: 'Received', className: 'text-emerald-700 bg-emerald-50' },
    closed_short: { label: 'Closed Short', className: 'text-red-700 bg-red-50' },
};

export const LineStatusPill: React.FC<{ status: PurchaseOrderLineStatus; className?: string }> = ({
    status, className,
}) => {
    const style = LINE_STATUS_STYLES[status] ?? LINE_STATUS_STYLES.open;
    return (
        <span className={cn(
            'inline-flex items-center text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap',
            style.className, className
        )}>
            {style.label}
        </span>
    );
};

/**
 * The "amendments waiting" flag. Deliberately loud: a PO the yard has asked to change but
 * AP has not answered yet is the one thing the AP queue must not let slip.
 */
export const AmendmentChip: React.FC<{ count: number; className?: string }> = ({ count, className }) => {
    if (!count) return null;
    return (
        <span className={cn(
            'inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md',
            'text-amber-800 bg-amber-100 ring-1 ring-amber-300',
            className
        )}>
            {count} amendment{count === 1 ? '' : 's'} requested
        </span>
    );
};

export const ClosedShortChip: React.FC<{ className?: string }> = ({ className }) => (
    <span className={cn(
        'inline-flex items-center text-xs font-medium px-2 py-1 rounded-md',
        'text-amber-800 bg-amber-50 ring-1 ring-amber-200', className
    )}>
        Closed Short
    </span>
);

/** Over-received reads as "100%+" — a lane card, a list row and the detail page must never
 * round it away, and never show a raw 133.3%. */
export const receivedPercentLabel = (percent: number | null) =>
    percent === null ? '—' : percent > 100 ? '100%+' : `${Math.round(percent)}%`;

/**
 * The compact "how much of this has landed" read-out, shared by the register's list, its
 * Kanban cards and the detail page, so a second copy cannot drift the moment one of them
 * learns about over-receipt.
 */
export const ReceivedProgress = ({
    percent,
    className,
}: {
    percent: number | null;
    className?: string;
}) => {
    if (percent === null) {
        return <span className={cn('text-muted-foreground', className)}>—</span>;
    }

    // Clamped for the bar only: the label keeps the truth about an over-received order.
    const filled = Math.max(0, Math.min(100, percent));

    return (
        <div className={cn('flex items-center gap-2', className)}>
            <div
                className="h-2 w-16 shrink-0 rounded-full bg-muted overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(filled)}
                aria-label="Received"
            >
                <div
                    className={cn('h-full rounded-full', percent > 100 ? 'bg-amber-500' : 'bg-primary')}
                    style={{ width: `${filled}%` }}
                />
            </div>
            <span className="text-xs tabular-nums whitespace-nowrap">
                {receivedPercentLabel(percent)}
            </span>
        </div>
    );
};
